/* eslint-disable react/display-name */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Tag,
  TagCloseButton,
  TagLabel,
  useToast,
  Skeleton,
  Link as ChakraLink,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  GetLimitedPartnerFacilitiesForLenderQueryGQLType,
  useGetLimitedPartnerMappingsForCreditFacilityLazyQueryGQLType,
  useUpdateLimitedPartnerMappingMutation,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { AddLimitedPartnerButton } from './AddLimitedPartnerButton';
import { LimitedPartnerSearch } from './LimitedPartnerSearch';
import { MatchStatus } from './types';
import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import {
  successToastConfig,
  errorToastConfig,
} from '~/packages/common-frontend/constants';

type HandleUpdateMappingProps = {
  mappingId: string;
  lpId: string | null;
};

const LimitedPartnerCell = ({
  limitedPartnerName,
  limitedPartnerId,
  limitedPartnerAlias,
  limitedPartnerMappingId,
  lenderOrganizationId,
  handleUpdateMapping,
  suggestedLimitedPartnerId,
  suggestedLimitedPartnerName,
}: {
  limitedPartnerName: string | null;
  limitedPartnerId: string | null;
  limitedPartnerAlias: string;
  limitedPartnerMappingId: string;
  lenderOrganizationId: string;
  handleUpdateMapping: ({ mappingId, lpId }: HandleUpdateMappingProps) => void;
  suggestedLimitedPartnerId: string | null;
  suggestedLimitedPartnerName: string | null;
}) => {
  const [limitedPartnerIdForMapping, setLimitedPartnerIdForMapping] = useState(
    limitedPartnerId,
  );
  const [
    limitedPartnerNameForMapping,
    setLimitedPartnerNameForMapping,
  ] = useState(limitedPartnerName);
  const [showManualSearch, setShowManualSearch] = useState(false);

  useEffect(() => {
    setLimitedPartnerIdForMapping(limitedPartnerId);
    setLimitedPartnerNameForMapping(limitedPartnerName);
  }, [limitedPartnerName, limitedPartnerId]);

  return (
    <>
      {limitedPartnerNameForMapping && limitedPartnerIdForMapping && (
        <Tag variant="solid-muted" colorScheme="green" size="lg">
          <Link
            to={`/limited-partners/details/${limitedPartnerIdForMapping}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: colors.blue[400],
              fontSize: '1rem',
              textDecoration: 'underline',
              wordBreak: 'break-all',
            }}
          >
            <TagLabel>{limitedPartnerNameForMapping}</TagLabel>
          </Link>

          <TagCloseButton
            onClick={() => {
              finleyAssert(
                limitedPartnerName,
                'Limited partner name should exist for mapping',
              );
              setLimitedPartnerIdForMapping(null);
              setLimitedPartnerNameForMapping(null);
              handleUpdateMapping({
                lpId: null,
                mappingId: limitedPartnerMappingId,
              });
            }}
          />
        </Tag>
      )}
      {limitedPartnerIdForMapping === null && (
        <Flex direction="column" gap="8px" alignItems="baseline">
          {suggestedLimitedPartnerId && suggestedLimitedPartnerName ? (
            <>
              {!showManualSearch ? (
                <>
                  <Flex alignItems="center" gap="8px">
                    <Tag
                      variant="solid-muted"
                      colorScheme="blue"
                      size="lg"
                      minWidth="0"
                      maxWidth="calc(100% - 60px)"
                    >
                      <Link
                        to={`/limited-partners/details/${suggestedLimitedPartnerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: colors.blue[400],
                          fontSize: '1rem',
                          textDecoration: 'underline',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <TagLabel>{suggestedLimitedPartnerName}</TagLabel>
                      </Link>
                    </Tag>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      flexShrink={0}
                      onClick={() => {
                        setLimitedPartnerIdForMapping(
                          suggestedLimitedPartnerId,
                        );
                        setLimitedPartnerNameForMapping(
                          suggestedLimitedPartnerName,
                        );
                        handleUpdateMapping({
                          lpId: suggestedLimitedPartnerId,
                          mappingId: limitedPartnerMappingId,
                        });
                      }}
                    >
                      Match
                    </Button>
                  </Flex>
                  <Button
                    variant="link"
                    size="sm"
                    color="gray.500"
                    textDecoration="underline"
                    onClick={() => setShowManualSearch(true)}
                  >
                    Choose different LP
                  </Button>
                </>
              ) : (
                <>
                  <LimitedPartnerSearch
                    lenderOrganizationId={lenderOrganizationId}
                    onSelect={(lpId, lpName) => {
                      setLimitedPartnerIdForMapping(lpId);
                      setLimitedPartnerNameForMapping(lpName);
                      handleUpdateMapping({
                        lpId,
                        mappingId: limitedPartnerMappingId,
                      });
                    }}
                    defaultQuery={limitedPartnerAlias}
                  />
                  <AddLimitedPartnerButton
                    onAddLimitedPartner={(params) => {
                      handleUpdateMapping({
                        lpId: params.lpId,
                        mappingId: limitedPartnerMappingId,
                      });
                    }}
                  />
                  <Button
                    variant="link"
                    size="sm"
                    color="gray.500"
                    textDecoration="underline"
                    onClick={() => setShowManualSearch(false)}
                  >
                    Back to suggestion
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <LimitedPartnerSearch
                lenderOrganizationId={lenderOrganizationId}
                onSelect={(lpId, lpName) => {
                  setLimitedPartnerIdForMapping(lpId);
                  setLimitedPartnerNameForMapping(lpName);
                  handleUpdateMapping({
                    lpId,
                    mappingId: limitedPartnerMappingId,
                  });
                }}
                defaultQuery={limitedPartnerAlias}
              />
              <AddLimitedPartnerButton
                onAddLimitedPartner={(params) => {
                  handleUpdateMapping({
                    lpId: params.lpId,
                    mappingId: limitedPartnerMappingId,
                  });
                }}
              />
            </>
          )}
        </Flex>
      )}
    </>
  );
};

type AliasMatchRow = {
  mappingId: string;
  limitedPartnerAlias: string;
  limitedPartnerId: string | null;
  limitedPartnerName: string | null;
  committedCapital: number;
  reportingDate: Util.FullDateStr;
  matchedByUserName: string | null;
  matchedByUserEmail: string | null;
  loanProductId: string;
  creditFacilityId: string;
  suggestedLimitedPartnerId: string | null;
  suggestedLimitedPartnerName: string | null;
};

const AliasMatchingTable = ({
  rows,
  lenderOrganizationId,
  handleUpdateMapping,
}: {
  rows: AliasMatchRow[];
  lenderOrganizationId: string;
  handleUpdateMapping: ({ mappingId, lpId }: HandleUpdateMappingProps) => void;
}) => {
  const columns = useMemo(() => {
    const tableColumns: ColumnDef<AliasMatchRow, any>[] = [
      {
        id: 'limitedPartnerAlias',
        accessorKey: 'limitedPartnerAlias',
        header: 'Limited Partner Alias',
      },
      {
        id: 'reportingDate',
        accessorKey: 'reportingDate',
        header: 'Reporting Date',
        cell: (cell) =>
          commonDate.fullDateStrToHumanDateString(
            cell.row.original.reportingDate,
          ),
      },
      {
        id: 'committedCapital',
        accessorKey: 'committedCapital',
        header: 'Committed Capital',
        cell: (cell) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: Number(cell.row.original.committedCapital),
          }),
      },
      {
        id: 'updatedBy',
        header: 'Matched By',
        cell: ({ row }) => (
          <>
            {row.original.matchedByUserName && (
              <Typography variant="detail-muted">
                {row.original.matchedByUserName}
              </Typography>
            )}
            {row.original.matchedByUserEmail && (
              <ChakraLink
                fontSize="sm"
                fontWeight="bold"
                color="blue.400"
                href={`mailto:${row.original.matchedByUserEmail}`}
                textDecoration="underline"
              >
                {row.original.matchedByUserEmail}
              </ChakraLink>
            )}
          </>
        ),
      },
      {
        id: 'limitedPartner',
        accessorKey: 'limitedPartnerName',
        header: 'Limited Partner',
        cell: ({ row }) => (
          <LimitedPartnerCell
            limitedPartnerId={row.original.limitedPartnerId}
            limitedPartnerName={row.original.limitedPartnerName}
            limitedPartnerAlias={row.original.limitedPartnerAlias}
            limitedPartnerMappingId={row.original.mappingId}
            lenderOrganizationId={lenderOrganizationId}
            handleUpdateMapping={handleUpdateMapping}
            suggestedLimitedPartnerId={row.original.suggestedLimitedPartnerId}
            suggestedLimitedPartnerName={
              row.original.suggestedLimitedPartnerName
            }
          />
        ),
      },
    ];

    return tableColumns;
  }, [handleUpdateMapping, lenderOrganizationId]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    getRowId: (row) => row.mappingId,
  });

  return <TableBETA table={table} />;
};

type FacilitySummary = GetLimitedPartnerFacilitiesForLenderQueryGQLType['limitedPartnerFacilities'][number];

/**
 * Effect-only helper: fires `onFirstExpand` the first time its accordion item
 * becomes expanded. Rendered inside the AccordionItem render-prop so we can
 * react to `isExpanded` without running side effects during render.
 */
const ExpandTrigger = ({
  isExpanded,
  onFirstExpand,
}: {
  isExpanded: boolean;
  onFirstExpand: () => void;
}) => {
  useEffect(() => {
    if (isExpanded) {
      onFirstExpand();
    }
  }, [isExpanded, onFirstExpand]);
  return null;
};

/**
 * A single facility accordion. Its mappings are fetched lazily the first time
 * the accordion is expanded (not up front), which keeps the initial page load
 * cheap for orgs with hundreds of facilities. The facility owns its own update
 * mutation and refetches only its own mappings (plus the parent facility counts)
 * after a match/unmatch, rather than reloading every facility.
 */
const FacilityAccordionItem = React.memo(
  ({
    facility,
    lenderOrganizationId,
    matchStatusForTables,
    refetchFacilities,
  }: {
    facility: FacilitySummary;
    lenderOrganizationId: string;
    matchStatusForTables: MatchStatus;
    refetchFacilities: () => void;
  }) => {
    const [hasBeenOpened, setHasBeenOpened] = useState(false);

    const [
      fetchMappings,
      {
        data: mappingsData,
        loading: mappingsLoading,
        refetch: refetchMappings,
      },
    ] = useGetLimitedPartnerMappingsForCreditFacilityLazyQueryGQLType({
      fetchPolicy: 'cache-and-network',
    });

    const refetchThisFacility = useCallback(() => {
      if (refetchMappings) {
        refetchMappings();
      }
      // keep the header badge / status cards in sync with the new match state
      refetchFacilities();
    }, [refetchMappings, refetchFacilities]);

    const handleExpand = useCallback(() => {
      if (!hasBeenOpened) {
        setHasBeenOpened(true);
        fetchMappings({
          variables: {
            lenderOrganizationId,
            creditFacilityId: facility.creditFacilityId,
          },
        });
      }
    }, [
      hasBeenOpened,
      fetchMappings,
      lenderOrganizationId,
      facility.creditFacilityId,
    ]);

    const rows: AliasMatchRow[] = useMemo(() => {
      const mappings =
        mappingsData?.limitedPartnerMappingsForCreditFacility ?? [];
      return mappings
        .filter(
          (mapping) =>
            matchStatusForTables === 'all' ||
            (matchStatusForTables === 'matched' && mapping.limitedPartnerId) ||
            (matchStatusForTables === 'notMatched' &&
              !mapping.limitedPartnerId),
        )
        .map((mapping) => ({
          mappingId: mapping.mappingId,
          limitedPartnerAlias: mapping.limitedPartnerAlias,
          limitedPartnerId: mapping.limitedPartnerId ?? null,
          limitedPartnerName: mapping.limitedPartnerName ?? null,
          committedCapital: mapping.committedCapital,
          reportingDate: mapping.reportingDate,
          matchedByUserEmail: mapping.matchedByUserEmail ?? null,
          matchedByUserName: mapping.matchedByUserName ?? null,
          loanProductId: mapping.loanProductId,
          creditFacilityId: mapping.creditFacilityId,
          suggestedLimitedPartnerId: mapping.suggestedLimitedPartnerId ?? null,
          suggestedLimitedPartnerName:
            mapping.suggestedLimitedPartnerName ?? null,
        }));
    }, [mappingsData, matchStatusForTables]);

    const autoMatchableCount = rows.filter(
      (m) => m.suggestedLimitedPartnerId && !m.limitedPartnerId,
    ).length;

    const toast = useToast();
    const bulkMappingIdsRef = useRef(new Set<string>());
    const bulkTotalRef = useRef(0);
    const bulkErrorCountRef = useRef(0);

    const [
      updateLimitedPartnerMapping,
    ] = useUpdateLimitedPartnerMappingMutation({
      onCompleted: (data) => {
        if (data && data.updateLimitedPartnerMapping) {
          const { limitedPartnerMappingId } = data.updateLimitedPartnerMapping;
          if (bulkMappingIdsRef.current.has(limitedPartnerMappingId)) {
            bulkMappingIdsRef.current.delete(limitedPartnerMappingId);
            if (bulkMappingIdsRef.current.size === 0) {
              const total = bulkTotalRef.current;
              const errors = bulkErrorCountRef.current;
              const succeeded = total - errors;
              if (succeeded > 0) {
                toast({
                  ...successToastConfig,
                  id: `auto-match-bulk-toast-${_.uniqueId()}`,
                  title: `Successfully auto-matched ${succeeded} ${
                    succeeded === 1 ? 'entry' : 'entries'
                  }.`,
                });
              }
              bulkTotalRef.current = 0;
              bulkErrorCountRef.current = 0;
              refetchThisFacility();
            }
            return;
          }
          const {
            limitedPartnerId,
            limitedPartnerAlias,
            limitedPartnerName,
          } = data.updateLimitedPartnerMapping;
          successToastConfig.id = `update-limited-partner-toast-${_.uniqueId(
            limitedPartnerMappingId,
          )}`;
          successToastConfig.title = `${limitedPartnerAlias} was ${
            limitedPartnerId ? 'matched with' : 'unmatched from'
          } ${limitedPartnerName}.`;
          toast(successToastConfig);
        }
      },
      onError: (err) => {
        if (bulkMappingIdsRef.current.size > 0) {
          bulkErrorCountRef.current += 1;
          const remainingIds = Array.from(bulkMappingIdsRef.current);
          bulkMappingIdsRef.current.delete(remainingIds[0]);
          if (bulkMappingIdsRef.current.size === 0) {
            const total = bulkTotalRef.current;
            const errors = bulkErrorCountRef.current;
            const succeeded = total - errors;
            if (succeeded > 0) {
              toast({
                ...successToastConfig,
                id: `auto-match-bulk-toast-${_.uniqueId()}`,
                title: `Auto-matched ${succeeded} of ${total} ${
                  total === 1 ? 'entry' : 'entries'
                }. ${errors} failed.`,
              });
            } else {
              toast({
                ...errorToastConfig,
                title: `Auto-match failed for all ${total} ${
                  total === 1 ? 'entry' : 'entries'
                }.`,
              });
            }
            bulkTotalRef.current = 0;
            bulkErrorCountRef.current = 0;
            refetchThisFacility();
          }
          return;
        }
        errorToastConfig.title = err.message;
        toast(errorToastConfig);
      },
    });

    const handleUpdateMapping = useCallback(
      ({ mappingId, lpId }: { mappingId: string; lpId: string | null }) => {
        updateLimitedPartnerMapping({
          variables: {
            limitedPartnerMappingId: mappingId,
            limitedPartnerId: lpId,
          },
        });
        setTimeout(refetchThisFacility, 150);
      },
      [refetchThisFacility, updateLimitedPartnerMapping],
    );

    const handleAutoMatchForFacility = useCallback((): void => {
      const autoMatchable = rows.filter(
        (m) => m.suggestedLimitedPartnerId && !m.limitedPartnerId,
      );
      bulkTotalRef.current = autoMatchable.length;
      bulkErrorCountRef.current = 0;
      bulkMappingIdsRef.current = new Set(
        autoMatchable.map((m) => m.mappingId),
      );
      for (const mapping of autoMatchable) {
        updateLimitedPartnerMapping({
          variables: {
            limitedPartnerMappingId: mapping.mappingId,
            limitedPartnerId: mapping.suggestedLimitedPartnerId,
          },
        });
      }
    }, [rows, updateLimitedPartnerMapping]);

    return (
      // Each facility gets its own single-item Accordion. Chakra distributes
      // open-state via context to every AccordionItem, so a shared Accordion
      // around all ~490 facilities re-renders all of them on each toggle (laggy).
      // Isolating one item per Accordion keeps a toggle scoped to that facility.
      <Accordion allowToggle mt="16px">
        <AccordionItem
          borderColor={colors.gray[200]}
          borderRadius="8px"
          borderWidth="1px"
        >
          {({ isExpanded }) => (
            <>
              <ExpandTrigger
                isExpanded={isExpanded}
                onFirstExpand={handleExpand}
              />
              <AccordionButton py="12px" px="16px">
                <Flex flex="1" alignItems="center" gap="8px">
                  <Typography
                    fontWeight="semibold"
                    variant="section-subtitle"
                    trim
                  >
                    {facility.creditFacilityName}
                  </Typography>
                  <Box
                    background={colors.green[50]}
                    borderRadius="4px"
                    padding="4px 8px"
                  >
                    <Typography
                      fontWeight="semibold"
                      variant="detail"
                      TextProps={{ color: colors.green[500] }}
                    >
                      {facility.matchedCount} matched
                    </Typography>
                  </Box>
                  <Box
                    background={colors.orange[50]}
                    borderRadius="4px"
                    padding="4px 8px"
                  >
                    <Typography
                      fontWeight="semibold"
                      variant="detail"
                      TextProps={{ color: colors.orange[500] }}
                    >
                      {facility.unmatchedCount} unmatched
                    </Typography>
                  </Box>
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb="16px" px="16px">
                {mappingsLoading && !mappingsData ? (
                  <Skeleton height="12rem" />
                ) : (
                  <>
                    {autoMatchableCount > 0 && (
                      <Alert
                        status="info"
                        variant="subtle"
                        borderRadius="6px"
                        mb="12px"
                      >
                        <AlertIcon />
                        <AlertDescription flex="1">
                          {autoMatchableCount}{' '}
                          {autoMatchableCount === 1
                            ? 'alias is'
                            : 'aliases are'}{' '}
                          ready to auto-match. They exactly match existing LP
                          names.
                        </AlertDescription>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={handleAutoMatchForFacility}
                        >
                          Match All
                        </Button>
                      </Alert>
                    )}
                    <AliasMatchingTable
                      rows={rows}
                      lenderOrganizationId={lenderOrganizationId}
                      handleUpdateMapping={handleUpdateMapping}
                    />
                  </>
                )}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    );
  },
);

type AliasingMatchingTablesProps = {
  facilities: FacilitySummary[];
  lenderOrganizationId: string;
  searchQuery: string;
  matchStatusForTables: MatchStatus;
  refetchFacilities: () => void;
};
const AliasMatchingTables = ({
  facilities,
  lenderOrganizationId,
  searchQuery,
  matchStatusForTables,
  refetchFacilities,
}: AliasingMatchingTablesProps) => {
  const filteredFacilities = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return facilities;
    }
    return facilities.filter((facility) =>
      facility.creditFacilityName.toLowerCase().includes(trimmedQuery),
    );
  }, [facilities, searchQuery]);

  return (
    <>
      {filteredFacilities.map((facility) => (
        <FacilityAccordionItem
          key={facility.creditFacilityId}
          facility={facility}
          lenderOrganizationId={lenderOrganizationId}
          matchStatusForTables={matchStatusForTables}
          refetchFacilities={refetchFacilities}
        />
      ))}
    </>
  );
};

export const AliasMatchingTablesContainer = ({
  facilities,
  lenderOrganizationId,
  searchQuery,
  matchStatusForTables,
  refetchFacilities,
}: Omit<AliasingMatchingTablesProps, 'facilities'> & {
  facilities: FacilitySummary[] | undefined;
}) => {
  return (
    <Box mt="2.5rem">
      {facilities ? (
        <AliasMatchingTables
          facilities={facilities}
          lenderOrganizationId={lenderOrganizationId}
          searchQuery={searchQuery}
          matchStatusForTables={matchStatusForTables}
          refetchFacilities={refetchFacilities}
        />
      ) : (
        <Skeleton height="80vh" />
      )}
    </Box>
  );
};
